import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useOrganization } from "./OrganizationContext";
import { supabase } from "@/lib/supabase";
import type { SupabaseClient } from "@supabase/supabase-js";
import { hexToHslComponents } from "@/lib/color";

export interface BrandingConfig {
  appName: string;
  logoUrl: string | null;
  primaryColor: string | null; // hex
  customDomain: string | null;
  hidePoweredBy: boolean;
}

const DEFAULT_BRANDING: BrandingConfig = {
  appName: "Relay",
  logoUrl: null,
  primaryColor: null,
  customDomain: null,
  hidePoweredBy: false,
};

interface BrandingContextValue {
  branding: BrandingConfig;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

function applyBrandingToDOM(config: BrandingConfig) {
  const root = document.documentElement;
  if (config.primaryColor) {
    const hsl = hexToHslComponents(config.primaryColor);
    if (hsl) {
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--ring", hsl);
    }
  } else {
    root.style.removeProperty("--primary");
    root.style.removeProperty("--ring");
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { organizationId } = useOrganization();
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    setIsLoading(true);
    void (supabase as unknown as SupabaseClient)
      .schema("relay")
      .from("org_branding")
      .select("app_name, logo_url, primary_color, custom_domain, hide_powered_by")
      .eq("organization_id", organizationId)
      .maybeSingle()
      .then(({ data }) => {
        const config: BrandingConfig = data
          ? {
              appName: data.app_name ?? "Relay",
              logoUrl: data.logo_url ?? null,
              primaryColor: data.primary_color ?? null,
              customDomain: data.custom_domain ?? null,
              hidePoweredBy: data.hide_powered_by ?? false,
            }
          : DEFAULT_BRANDING;
        setBranding(config);
        applyBrandingToDOM(config);
        setIsLoading(false);
      });

    return () => {
      // Revert DOM overrides when org changes or component unmounts
      const root = document.documentElement;
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
    };
  }, [organizationId]);

  const value = useMemo(() => ({ branding, isLoading }), [branding, isLoading]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used inside BrandingProvider");
  return ctx;
}
